/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  composeSetters,
  fieldValidationSetter,
  mergeArraysByKey,
  removeFromArrayReducer,
  setDirty,
  setEntityField,
  setNewDirtyType,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  updateInArrayReducer,
} from '@gh/common-shared';
import {
  complementHpeCuttingListItem,
  getHpeCuttingListItemId,
  HpeCuttingListItem,
  isHpeCuttingListItemFieldAffectsPrice,
  validateHpeCuttingListItemField,
} from '../../../resources/hpe-details.model';
import {
  HpeDetailsState,
  HpeDetailsStateCuttingListItemRemoveEvent,
  HpeDetailsStateCuttingListItemSetFieldEvent,
  HpeDetailsStateEvent,
  HpeDetailsStateEventType,
} from '../hpe-details.state';

export const mergeCuttingList = mergeArraysByKey<HpeCuttingListItem>('sortOrder');

const setCuttingListItemField = composeSetters(
  setEntityField,
  setUpdatedDirtyType,
  fieldValidationSetter(validateHpeCuttingListItemField));

const byCuttingListItem = (event: HpeDetailsStateCuttingListItemRemoveEvent | HpeDetailsStateCuttingListItemSetFieldEvent,
                           item: HpeCuttingListItem) => getHpeCuttingListItemId(item) === event.cuttingId;
const cuttingListItemReducer = (state: HpeCuttingListItem, event: HpeDetailsStateCuttingListItemSetFieldEvent) =>
  setCuttingListItemField(state, event.name, event.value);

const cuttingListUpdateReducer = updateInArrayReducer(cuttingListItemReducer, byCuttingListItem);
const cuttingListRemoveReducer = removeFromArrayReducer(byCuttingListItem);

export function cuttingListReducer(cuttingList: HpeCuttingListItem[],
                                   event: HpeDetailsStateEvent): HpeCuttingListItem[] {
  switch (event.type) {
    case HpeDetailsStateEventType.CuttingListItemAdd:
      return reorderCuttingList([<HpeCuttingListItem>complementHpeCuttingListItem(setNewDirtyType({
        ...event.item,
        sortOrder: 0,
      }))].concat(cuttingList));
    case HpeDetailsStateEventType.CuttingListItemRemove:
      return reorderCuttingList(cuttingListRemoveReducer(cuttingList, event));
    case HpeDetailsStateEventType.CuttingListItemSetField:
      return cuttingListUpdateReducer(cuttingList, event);
    default:
      return cuttingList;
  }
}

export function rootCuttingListReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemRemove:
      return setRecalculationNeeded(setDirty(state));
    case HpeDetailsStateEventType.CuttingListItemSetField:
      if (isHpeCuttingListItemFieldAffectsPrice(event.name)) {
        return setRecalculationNeeded(setDirty(state));
      } else {
        return setDirty(state);
      }
    default:
      return state;
  }
}

function reorderCuttingList(cuttingList: HpeCuttingListItem[]): HpeCuttingListItem[] {
  return cuttingList.map((item, i) => setEntityField(item, 'sortOrder', i));
}
